import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { Page } from "containers/Page";
import { PrimaryButton } from "components/Button/style";
import { toastErro, toastAtencao } from "components/Toast";
import { alertaWarnig } from "components/Alerta";
import { IEstampagemPlacaItem } from "../../interfaces/ILoteEstampagem";
import { CameraCapture } from "../../modals/AdicionarPlacaModal/components/CameraCapture";
import { TagInput } from "../../modals/AdicionarPlacaModal/components/TagInput";
import { useGerenciarLote } from "./hooks/useGerenciarLote";
import { EstadoFluxo, IAlertaLote, IPlacaImportacao } from "./types";
import { ImpressoraBar } from "./components/impressora-bar";
import { LoteInfoCard } from "./components/lote-info-card";
import { ConfirmarPlaca } from "./components/confirmar-placa";
import { ConfirmarTagAuto } from "./components/confirmar-tag-auto";
import { AlertaLote } from "./components/alerta-lote";
import { ImportarPlanilha } from "./components/importar-planilha";
import { ResultadoImportacaoModal } from "./components/resultado-importacao-modal";
import { ItensTable } from "./components/itens-table";

const PLACA_REGEX = /^[A-Z]{3}[0-9]([A-Z][0-9]{2}|[0-9]{3})$/;

function ChassiBarcodeInput({ totalItens, loading, onChassi }: { totalItens: number; loading: boolean; onChassi: (chassi: string) => Promise<void> }) {
  const [valor, setValor] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLoadingRef = useRef(false);

  useEffect(() => { inputRef.current?.focus(); return () => { if (timerRef.current) { clearTimeout(timerRef.current); } }; }, []);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) { inputRef.current?.focus(); }
    prevLoadingRef.current = loading;
  }, [loading]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.toUpperCase();
    setValor(v);
    if (timerRef.current) { clearTimeout(timerRef.current); }
    if (v.trim().length > 0) {
      timerRef.current = setTimeout(() => { onChassi(v.trim()); setValor(""); }, 300);
    }
  }

  return (
    <div className="text-center py-4">
      <p className="text-muted mb-1" style={{ fontSize: "0.85rem" }}>Itens registrados neste lote</p>
      <div style={{ fontSize: "2.5rem", fontWeight: 800, marginBottom: "1.5rem", color: "#1b3c6e" }}>{totalItens}</div>
      <p className="text-muted mb-2">Aproxime ou escaneie o código de barras (placa ou chassi):</p>
      <input
        ref={inputRef}
        type="text"
        className="form-control text-center"
        style={{ fontSize: "1.2rem", fontWeight: 600, letterSpacing: 2, maxWidth: 360, margin: "0 auto" }}
        placeholder="Aguardando leitura..."
        value={valor}
        onChange={handleChange}
        disabled={loading}
        autoFocus
      />
      {loading && (
        <div className="text-center mt-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Vinculando chassi...</span>
        </div>
      )}
    </div>
  );
}

const ESTADOS_LEITURA: EstadoFluxo[] = ["camera", "processando", "confirmar_placa", "leitura_tag", "erro", "confirmar_tag_auto", "alerta_lote", "leitura_codigo_barras"];

const TESTE_ETIQUETA = {
  empresa: "Empresa Teste",
  placa: "TEST001",
  data: new Date().toLocaleDateString("pt-BR"),
  marcaModelo: "MARCA/MODELO TESTE",
  cor: "BRANCO",
  localLacre: "LOCAL TESTE",
  chassi: "9BGEA48A0SG000000",
};

export function GerenciarLote() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const idLote = Number(id);

  const {
    lote,
    itens,
    loading,
    loadingFinalizar,
    impressora,
    setImpressora,
    erroImpressora,
    impressoras,
    verificarImpressora,
    carregarImpressoras,
    recarregarItens,
    finalizarLote,
    validarPlaca,
    validarTag,
    adicionarItem,
    adicionarItemPorChassi,
    atualizarItem,
    removerItem,
    exportarLote,
    buscarTagPorPlaca,
    buscarProcessoPorPlaca,
    lerPlaca,
    buscarDadosEtiqueta,
    imprimirEtiqueta,
    imprimirEtiquetaPdf,
    atualizarStatusImpressao,
    abrirProcesso,
  } = useGerenciarLote(idLote);

  const [estado, setEstado] = useState<EstadoFluxo>("lista");
  const [mensagemErro, setMensagemErro] = useState<string | null>(null);
  const [alertaLote, setAlertaLote] = useState<IAlertaLote | null>(null);
  const [placaAtual, setPlacaAtual] = useState<string>("");
  const [placaConfirmacao, setPlacaConfirmacao] = useState<string>("");
  const [itemAtual, setItemAtual] = useState<IEstampagemPlacaItem | null>(null);
  const [loadingTag, setLoadingTag] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [loadingExportar, setLoadingExportar] = useState(false);
  const [loadingImportar, setLoadingImportar] = useState(false);
  const [importarProgresso, setImportarProgresso] = useState<{ atual: number; total: number } | null>(null);
  const [resultadoImportacao, setResultadoImportacao] = useState<IPlacaImportacao[] | null>(null);
  const [tagAutoInfo, setTagAutoInfo] = useState<{ placa: string; tag: string } | null>(null);
  const [tagAutoContador, setTagAutoContador] = useState(5);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const placaInputRef = useRef<HTMLInputElement>(null);
  const abrirLeituraRef = useRef<boolean>(!!(location.state as any)?.abrirLeitura);
  const origemRef = useRef<'camera' | 'leitura_codigo_barras'>('camera');

  const showModalLeitura = (ESTADOS_LEITURA as string[]).includes(estado);
  const fecharModalLeitura = useCallback(() => setEstado("lista"), []);

  // Countdown do estado confirmar_tag_auto
  useEffect(() => {
    if (estado !== "confirmar_tag_auto") { return; }
    setTagAutoContador(5);
    const estadoProximo = origemRef.current;
    const intervalo = setInterval(() => {
      setTagAutoContador((c) => {
        if (c <= 1) {
          clearInterval(intervalo);
          setEstado(estadoProximo);
          setTagAutoInfo(null);
          setPlacaAtual("");
          setItemAtual(null);
          return 5;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(intervalo);
  }, [estado]);

  useEffect(() => {
    if (!lote || !abrirLeituraRef.current) { return; }
    abrirLeituraRef.current = false;
    if (lote.status === "ABERTO") {
      origemRef.current = 'leitura_codigo_barras';
      setEstado('leitura_codigo_barras');
    }
  }, [lote]);

  // ── Fluxo de registro ────────────────────────────────────────────────────

  const registrarPlaca = useCallback(async (placa: string, idProcesso?: string, tagPreExistente?: string) => {
    if (!lote) { return; }
    if (itens.some((i) => i.placa === placa)) {
      setMensagemErro(`Placa ${placa} já está neste lote.`);
      setEstado("erro");
      setLoadingGlobal(false);
      return;
    }
    setLoadingGlobal(true);
    try {
      if (idProcesso) {
        try {
          const dados = await buscarDadosEtiqueta(placa);
          await atualizarStatusImpressao(idProcesso, "IMPRIMINDO");
          await imprimirEtiqueta(dados);
          await atualizarStatusImpressao(idProcesso, "IMPRESSO");
        } catch {
          // falha na impressão não bloqueia o fluxo
        }
      }
      const item = await adicionarItem({ placa, idProcesso });
      setPlacaAtual(placa);
      setItemAtual(item);
      await recarregarItens();

      if (lote.comTag) {
        if (tagPreExistente) {
          await atualizarItem(item.id, tagPreExistente);
          await recarregarItens();
          setTagAutoInfo({ placa, tag: tagPreExistente });
          setEstado("confirmar_tag_auto");
          setLoadingGlobal(false);
          return;
        }
        if (lote.tagJaRegistrada) {
          try {
            const tagExistente = await buscarTagPorPlaca(placa);
            if (tagExistente) {
              await atualizarItem(item.id, tagExistente);
              await recarregarItens();
              setTagAutoInfo({ placa, tag: tagExistente });
              setEstado("confirmar_tag_auto");
              setLoadingGlobal(false);
              return;
            }
          } catch {
            // fallthrough para leitura manual
          }
        }
        setEstado("leitura_tag");
      } else {
        setEstado("camera");
        setPlacaAtual("");
        setItemAtual(null);
      }
    } catch (error: any) {
      setMensagemErro(error?.response?.data?.message ?? error?.message ?? "Erro ao registrar placa");
      setEstado("erro");
    } finally {
      setLoadingGlobal(false);
    }
  }, [lote, itens, buscarDadosEtiqueta, imprimirEtiqueta, atualizarStatusImpressao, adicionarItem, recarregarItens, buscarTagPorPlaca, atualizarItem]);

  const processarPlaca = useCallback(async (placa: string) => {
    if (!lote) { return; }
    if (!PLACA_REGEX.test(placa)) { setEstado("camera"); return; }
    setLoadingGlobal(true);
    setMensagemErro(null);
    try {
      const validacao = await validarPlaca(placa);
      if (validacao.naoEstampada) {
        setMensagemErro(`Placa ${placa} não foi estampada.`);
        setEstado("erro");
        setLoadingGlobal(false);
        return;
      }
      if (validacao.emLote) {
        if (validacao.emLoteTipo === "TAGS") {
          await registrarPlaca(placa, validacao.idProcesso, validacao.tagExistente);
        } else if (lote.comTag && lote.tagJaRegistrada) {
          const tagExistente = await buscarTagPorPlaca(placa);
          await registrarPlaca(placa, validacao.idProcesso, tagExistente ?? undefined);
        } else {
          setAlertaLote({ idLote: validacao.idLote, nomeEmpresa: validacao.nomeEmpresa, idProcesso: validacao.idProcesso, placa });
          setPlacaAtual(placa);
          setEstado("alerta_lote");
          setLoadingGlobal(false);
        }
        return;
      }
      await registrarPlaca(placa, validacao.idProcesso);
    } catch (error: any) {
      setMensagemErro(error?.response?.data?.message ?? error?.message ?? "Erro ao processar placa");
      setEstado("erro");
      setLoadingGlobal(false);
    }
  }, [lote, validarPlaca, registrarPlaca, buscarTagPorPlaca]);

  const handleCaptura = useCallback(async (imagemBase64: string) => {
    origemRef.current = 'camera';
    setEstado("processando");
    try {
      const placa = await lerPlaca(imagemBase64);
      if (!placa?.trim()) { setEstado("camera"); return; }
      await processarPlaca(placa.toUpperCase());
    } catch {
      setEstado("camera");
    }
  }, [lerPlaca, processarPlaca]);

  const handleTag = useCallback(async (tag: string) => {
    if (!itemAtual || !lote) { return; }
    setLoadingTag(true);
    try {
      const resultado = await validarTag(tag);
      if (resultado.vinculada) {
        toastAtencao(`Tag já está vinculada à placa ${resultado.placa}.`);
        setLoadingTag(false);
        return;
      }
      await atualizarItem(itemAtual.id, tag);
      await recarregarItens();
      setEstado(origemRef.current);
      setPlacaAtual("");
      setItemAtual(null);
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao vincular tag");
    } finally {
      setLoadingTag(false);
    }
  }, [itemAtual, lote, validarTag, atualizarItem, recarregarItens]);

  // ── Fluxo código de barras (chassi) ─────────────────────────────────────

  const handleChassi = useCallback(async (chassi: string) => {
    if (!lote) { return; }
    origemRef.current = 'leitura_codigo_barras';
    setLoadingGlobal(true);
    setMensagemErro(null);
    try {
      const item = await adicionarItemPorChassi(chassi);
      await recarregarItens();

      if (lote.comTag) {
        if (lote.tagJaRegistrada) {
          try {
            const tagExistente = await buscarTagPorPlaca(item.placa);
            if (tagExistente) {
              await atualizarItem(item.id, tagExistente);
              await recarregarItens();
              setTagAutoInfo({ placa: item.placa, tag: tagExistente });
              setEstado("confirmar_tag_auto");
              setLoadingGlobal(false);
              return;
            }
          } catch {
            // fallthrough para leitura manual
          }
        }
        setPlacaAtual(item.placa);
        setItemAtual(item);
        setEstado("leitura_tag");
      } else {
        setEstado("leitura_codigo_barras");
      }
    } catch (error: any) {
      setMensagemErro(error?.response?.data?.message ?? error?.message ?? "Erro ao vincular chassi");
      setEstado("erro");
    } finally {
      setLoadingGlobal(false);
    }
  }, [lote, adicionarItemPorChassi, recarregarItens, buscarTagPorPlaca, atualizarItem]);

  // ── Importar planilha ────────────────────────────────────────────────────

  const handleBaixarModelo = useCallback(() => {
    const ws = XLSX.utils.aoa_to_sheet([["Placa"]]);
    ws["!cols"] = [{ wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Placas");
    XLSX.writeFile(wb, "modelo-importacao-tags.xlsx");
  }, []);

  const handleImportarPlanilha = useCallback(async (file: File) => {
    setLoadingImportar(true);
    setImportarProgresso(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const placas: string[] = [];
      for (const row of rows) {
        const chave = Object.keys(row).find((k) => k.trim().toUpperCase() === "PLACA");
        if (!chave) { continue; }
        const val = String(row[chave] ?? "").trim().toUpperCase();
        if (val) { placas.push(val); }
      }
      if (placas.length === 0) {
        toastErro('Nenhuma placa encontrada. Verifique se a planilha possui a coluna "Placa".');
        return;
      }
      const contagem: Record<string, number> = {};
      for (const p of placas) { contagem[p] = (contagem[p] ?? 0) + 1; }
      const placasUnicas = [...new Set(placas)];
      const duplicadas = new Set(placasUnicas.filter((p) => contagem[p] > 1));

      setImportarProgresso({ atual: 0, total: placasUnicas.length });
      const resultados: IPlacaImportacao[] = [];
      for (let idx = 0; idx < placasUnicas.length; idx++) {
        const placa = placasUnicas[idx];
        setImportarProgresso({ atual: idx + 1, total: placasUnicas.length });
        if (duplicadas.has(placa)) {
          resultados.push({ placa, idProcesso: null, erro: true, duplicada: true });
          continue;
        }
        try {
          let idProcesso = await buscarProcessoPorPlaca(placa);
          if (!idProcesso) {
            const validacao = await validarPlaca(placa);
            idProcesso = validacao.idProcesso ?? null;
          }
          const tagExistente = idProcesso ? await buscarTagPorPlaca(placa) : null;
          resultados.push({ placa, idProcesso, erro: !idProcesso, jaComTag: !!tagExistente });
        } catch {
          resultados.push({ placa, idProcesso: null, erro: true });
        }
      }
      setImportarProgresso(null);
      setResultadoImportacao(resultados);
      if (fileInputRef.current) { fileInputRef.current.value = ""; }
    } catch (error: any) {
      toastErro(error?.message ?? "Erro ao validar planilha");
      setImportarProgresso(null);
    } finally {
      setLoadingImportar(false);
    }
  }, [buscarProcessoPorPlaca, validarPlaca, buscarTagPorPlaca]);

  const handleProcessarImportacao = useCallback(async () => {
    if (!resultadoImportacao) { return; }
    const validas = resultadoImportacao.filter((r) => !r.erro && r.idProcesso);
    if (validas.length === 0) { return; }
    setLoadingImportar(true);
    try {
      for (const item of validas) {
        try { await adicionarItem({ placa: item.placa, idProcesso: item.idProcesso! }); } catch { /* ignora duplicatas */ }
      }
      await recarregarItens();
      setResultadoImportacao(null);
      setEstado("vincular_tags");
    } catch (error: any) {
      toastErro(error?.message ?? "Erro ao processar importação");
    } finally {
      setLoadingImportar(false);
    }
  }, [resultadoImportacao, adicionarItem, recarregarItens]);

  // ── TAGS: vincular tags em sequência ────────────────────────────────────

  const itensSemTag = itens.filter((i) => !i.tag);
  const itemVinculacao = itensSemTag[0] ?? null;

  const handleTagVinculacao = useCallback(async (tag: string) => {
    if (!itemVinculacao) { return; }
    setLoadingTag(true);
    try {
      const resultado = await validarTag(tag);
      if (resultado.vinculada) {
        toastAtencao(`Tag já está vinculada à placa ${resultado.placa}.`);
        setLoadingTag(false);
        return;
      }
      await atualizarItem(itemVinculacao.id, tag);
      await recarregarItens();
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao vincular tag");
    } finally {
      setLoadingTag(false);
    }
  }, [itemVinculacao, validarTag, atualizarItem, recarregarItens]);

  // ── Guards ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Page lastPage="Estampagem de Placa" title="Gerenciar Lote">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Page>
    );
  }

  if (!lote) {
    return (
      <Page lastPage="Estampagem de Placa" title="Gerenciar Lote">
        <Alert variant="danger" className="m-3">Lote não encontrado.</Alert>
      </Page>
    );
  }

  return (
    <Page
      lastPage="Home / Backoffice / Estampagem de Placa"
      title={`${lote.tipo === "TAGS" ? "Tags" : "Estampagem"} #${lote.id} — ${lote.nomeEmpresa}`}
    >
      {loadingGlobal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
          }}
        >
          <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
          <p className="text-white mt-3">Processando...</p>
        </div>
      )}

      <ImpressoraBar
        erroImpressora={erroImpressora}
        impressora={impressora}
        impressoras={impressoras}
        lote={lote}
        testeEtiqueta={TESTE_ETIQUETA}
        onVerificar={verificarImpressora}
        onCarregarImpressoras={carregarImpressoras}
        onSelectImpressora={setImpressora}
        onImprimirPdf={imprimirEtiquetaPdf}
      />

      <div className="px-3 pt-3">
        {/* Barra de navegação e ações */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2 align-items-center">
            <PrimaryButton outline variante="primary" onClick={() => navigate("/backoffice/estampagem-placa")}>
              ← Voltar
            </PrimaryButton>
            
            {estado !== "lista" && !showModalLeitura && (
              <PrimaryButton outline variante="primary" onClick={() => setEstado("lista")}>
                Ver Lista
              </PrimaryButton>
            )}
          </div>

          {estado === "lista" && (
            <div className="d-flex gap-2 flex-wrap">
              {itens.length > 0 && (
                <PrimaryButton
                  outline
                  variante="primary"
                  disabled={loadingExportar}
                  onClick={async () => {
                    setLoadingExportar(true);
                    try { await exportarLote(); } catch (error: any) { toastErro(error?.message ?? "Erro ao exportar"); } finally { setLoadingExportar(false); }
                  }}
                >
                  {loadingExportar ? <Spinner animation="border" size="sm" /> : "Exportar Excel"}
                </PrimaryButton>
              )}
              {lote.status === "ABERTO" && lote.tipo === "TAGS" && (
                <>
                  <PrimaryButton outline variante="primary" onClick={() => setEstado("importar_planilha")}>
                    Importar Planilha
                  </PrimaryButton>
                  {itensSemTag.length > 0 && (
                    <PrimaryButton variante="primary" onClick={() => setEstado("vincular_tags")}>
                      Vincular Tags ({itensSemTag.length})
                    </PrimaryButton>
                  )}
                </>
              )}
              {lote.status === "ABERTO" && lote.tipo === "ESTAMPAGEM" && (
                <>
                  <PrimaryButton
                    buttonWidth="md"
                    buttonWidthTipo="px"
                    onClick={async () => {
                      if (!impressora) {
                        const { isConfirmed } = await alertaWarnig(
                          "Impressora não conectada",
                          "Deseja adicionar a placa sem imprimir a etiqueta?",
                          { showCancelButton: true, confirmButtonText: "Continuar mesmo assim" }
                        );
                        if (!isConfirmed) { return; }
                      }
                      origemRef.current = 'camera';
                      setEstado("camera");
                    }}
                  >
                    + Adicionar Placa
                  </PrimaryButton>
                  <PrimaryButton
                    buttonWidth="md"
                    buttonWidthTipo="px"
                    outline
                    variante="primary"
                    onClick={() => { origemRef.current = 'leitura_codigo_barras'; setEstado("leitura_codigo_barras"); }}
                  >
                    + Ler Código de Barras
                  </PrimaryButton>
                </>
              )}
              {lote.status === "ABERTO" && (
                <PrimaryButton disabled={loadingFinalizar} onClick={finalizarLote}>
                  {loadingFinalizar ? <Spinner animation="border" size="sm" /> : "Finalizar"}
                </PrimaryButton>
              )}
            </div>
          )}
        </div>

        <LoteInfoCard lote={lote} itensCount={itens.length} />

        {/* Estados */}
        {estado === "lista" && (
          <ItensTable
            itens={itens}
            lote={lote}
            onAbrirProcesso={abrirProcesso}
            onRemoverItem={removerItem}
            onRecarregar={recarregarItens}
          />
        )}

        {estado === "importar_planilha" && (
          <ImportarPlanilha
            loading={loadingImportar}
            progresso={importarProgresso}
            fileInputRef={fileInputRef}
            onBaixarModelo={handleBaixarModelo}
            onArquivo={handleImportarPlanilha}
          />
        )}

        {estado === "vincular_tags" && itemVinculacao && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                Faltam <strong>{itensSemTag.length}</strong> de {itens.length} placas
              </span>
              <PrimaryButton outline variante="primary" buttonWidth="sm" buttonWidthTipo="px" onClick={() => setEstado("lista")}>
                Parar
              </PrimaryButton>
            </div>
            <TagInput
              key={itemVinculacao.id}
              placa={itemVinculacao.placa}
              loading={loadingTag}
              onTag={handleTagVinculacao}
            />
          </div>
        )}

        {estado === "vincular_tags" && !itemVinculacao && (
          <div className="text-center py-4">
            <Alert variant="success">Todas as placas já possuem tag vinculada!</Alert>
            <PrimaryButton outline variante="primary" onClick={() => setEstado("lista")}>
              Voltar para lista
            </PrimaryButton>
          </div>
        )}
      </div>

      <Modal
        show={showModalLeitura}
        onHide={fecharModalLeitura}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>
            {origemRef.current === 'leitura_codigo_barras' ? "Leitura de Código de Barras" : "Leitura de Placa"}
          </Modal.Title>
          <Button variant="outline-danger" size="sm" onClick={fecharModalLeitura}>
            Encerrar leitura
          </Button>
        </Modal.Header>
        <Modal.Body>
          {estado === "camera" && (
            <CameraCapture
              ativa
              onCaptura={handleCaptura}
              onCapturaManual={async (imagemBase64) => {
                setEstado("processando");
                try {
                  const placa = await lerPlaca(imagemBase64);
                  setPlacaConfirmacao(placa?.trim() ? placa.toUpperCase() : "");
                  setEstado("confirmar_placa");
                } catch {
                  setPlacaConfirmacao("");
                  setEstado("confirmar_placa");
                }
              }}
              onErro={(msg) => { setMensagemErro(msg); setEstado("erro"); }}
            />
          )}

          {estado === "processando" && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
              <p className="mt-3 text-muted">Identificando placa...</p>
            </div>
          )}

          {estado === "confirmar_placa" && (
            <ConfirmarPlaca
              placa={placaConfirmacao}
              inputRef={placaInputRef}
              onChange={setPlacaConfirmacao}
              onConfirmar={processarPlaca}
              onVoltar={() => setEstado("camera")}
            />
          )}

          {estado === "leitura_tag" && (
            <TagInput
              placa={placaAtual}
              loading={loadingTag}
              onTag={handleTag}
              onEditarPlaca={origemRef.current !== 'leitura_codigo_barras' ? () => { setPlacaConfirmacao(placaAtual); setEstado("confirmar_placa"); } : undefined}
            />
          )}

          {estado === "erro" && (
            <>
              <Alert variant="danger">{mensagemErro ?? "Erro desconhecido."}</Alert>
              <div className="d-flex gap-2">
                <PrimaryButton outline variante="primary" onClick={() => setEstado(origemRef.current)}>Continuar lendo</PrimaryButton>
                <PrimaryButton outline variante="primary" onClick={fecharModalLeitura}>Encerrar</PrimaryButton>
              </div>
            </>
          )}

          {estado === "confirmar_tag_auto" && tagAutoInfo && (
            <ConfirmarTagAuto
              placa={tagAutoInfo.placa}
              tag={tagAutoInfo.tag}
              contador={tagAutoContador}
              onProxima={() => { setEstado(origemRef.current); setTagAutoInfo(null); }}
            />
          )}

          {estado === "alerta_lote" && alertaLote && (
            <AlertaLote
              alerta={alertaLote}
              onCancelar={() => setEstado("camera")}
              onConfirmar={() => registrarPlaca(alertaLote.placa, alertaLote.idProcesso)}
            />
          )}

          {estado === "leitura_codigo_barras" && (
            <ChassiBarcodeInput
              totalItens={itens.length}
              loading={loadingGlobal}
              onChassi={handleChassi}
            />
          )}
        </Modal.Body>
      </Modal>

      {resultadoImportacao && (
        <ResultadoImportacaoModal
          resultado={resultadoImportacao}
          loading={loadingImportar}
          onRemover={(placa) => setResultadoImportacao((prev) => prev ? prev.filter((r) => r.placa !== placa) : null)}
          onCancelar={() => setResultadoImportacao(null)}
          onProsseguir={handleProcessarImportacao}
        />
      )}
    </Page>
  );
}
